import { useState, useEffect, useRef } from "react";
import { KeyRound, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";

/**
 * Modal de autenticação por Senha Técnica.
 *
 * Quando o operador tenta acessar uma tela protegida (Dispositivos, Grupos de Acesso),
 * o componente pai abre este modal. Após validação no backend, `onSuccess()` é chamado
 * e o pai navega para a URL desejada.
 *
 * O backend (`POST /api/cliente/auth/validar-senha-tecnica`) retorna:
 *  - { ok: true, configurada: true } → liberar
 *  - { ok: false, configurada: false } → senha técnica nunca foi configurada — bloquear
 *  - { ok: false, configurada: true } → credenciais inválidas
 */
export default function SenhaTecnicaModal({ open, onClose, onSuccess, targetLabel }) {
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [readOnlyTrick, setReadOnlyTrick] = useState(true);
  const inputRef = useRef(null);
  // Nomes únicos por abertura → desestimulam o autofill do navegador a sugerir
  // o login/senha salvos da tela inicial de autenticação.
  const fieldNamesRef = useRef({ login: "tl", senha: "ts" });

  useEffect(() => {
    if (open) {
      setLogin("");
      setSenha("");
      setErro("");
      setReadOnlyTrick(true);
      // Gera nomes únicos a cada abertura (driblar autofill mais agressivo).
      const rand = Math.random().toString(36).slice(2, 8);
      fieldNamesRef.current = { login: `tl_${rand}`, senha: `ts_${rand}` };
      setTimeout(() => {
        // Remove readOnly e foca após o navegador desistir do autofill.
        setReadOnlyTrick(false);
        inputRef.current?.focus();
      }, 120);
    }
  }, [open]);

  if (!open) return null;

  const validar = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    if (!login.trim() || !senha.trim()) {
      setErro("Informe login e senha técnica.");
      return;
    }
    setLoading(true);
    setErro("");
    try {
      const r = await clienteApi.post("/cliente/auth/validar-senha-tecnica", {
        login: login.trim(),
        senha: senha.trim(),
      });
      if (r.data?.ok) {
        toast.success("Senha técnica validada");
        onSuccess?.();
        onClose?.();
        return;
      }
      if (r.data?.configurada === false) {
        setErro("Senha Técnica não configurada. Solicite ao administrador.");
      } else {
        setErro(r.data?.detail || "Login ou senha técnica inválidos.");
      }
    } catch (err) {
      setErro(err?.response?.data?.detail || "Erro ao validar senha técnica.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="senha-tecnica-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md mx-4 rounded-xl border border-brand-ice/15 bg-brand-dark/95 shadow-2xl p-5"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30">
              <KeyRound className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-heading font-bold text-brand-ice">
                Senha Técnica
              </h2>
              <p className="text-xs text-brand-ice/60">
                Acesso restrito{targetLabel ? ` — ${targetLabel}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="senha-tecnica-close"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-brand-ice/60 hover:text-brand-ice transition"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={validar} className="space-y-3" autoComplete="off">
          {/* Honeypots ocultos — alguns navegadores preenchem os primeiros
              campos username/password do form; com estes "iscas" antes dos campos
              reais, o autofill cai aqui em vez de poluir o login técnico. */}
          <input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />
          <input type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", opacity: 0, height: 0, width: 0, pointerEvents: "none" }} />
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-heading font-bold text-brand-ice/70 mb-1">
              Login Técnico
            </label>
            <input
              ref={inputRef}
              data-testid="senha-tecnica-login"
              type="text"
              name={fieldNamesRef.current.login}
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              readOnly={readOnlyTrick}
              onFocus={() => setReadOnlyTrick(false)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-brand-ice/20 text-brand-ice text-sm focus:outline-none focus:border-brand-light"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-heading font-bold text-brand-ice/70 mb-1">
              Senha Técnica
            </label>
            <input
              data-testid="senha-tecnica-senha"
              type="password"
              name={fieldNamesRef.current.senha}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              readOnly={readOnlyTrick}
              onFocus={() => setReadOnlyTrick(false)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-brand-ice/20 text-brand-ice text-sm focus:outline-none focus:border-brand-light"
            />
          </div>

          {erro && (
            <p data-testid="senha-tecnica-erro" className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {erro}
            </p>
          )}

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              data-testid="senha-tecnica-cancelar"
              onClick={onClose}
              className="flex-1 px-3 py-2 rounded-lg text-sm text-brand-ice/80 bg-white/5 hover:bg-white/10 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="senha-tecnica-confirmar"
              disabled={loading}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-brand-medium hover:bg-brand-dark text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Liberar acesso
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
